/**
 * GsplatAudioReactive — audio-reactive shader effect for gaussian splats.
 *
 * The scene moves to the music: bass drives a radial "breathing" pulse, mids drive a
 * ripple wave propagating outward, overall level drives a spatially-coherent wobble/warp,
 * and treble drives hue rotation. Global tracks affect the whole splat; positional audio is
 * analysed independently and affects only the authored source area.
 *
 * Architecture: a plain per-frame driver (like GsplatWeather), NOT a pc.createScript attached
 * to the splat entity — the entity is destroyed on every splat-swap path, while this driver
 * resolves the unified template material each frame and survives all swaps.
 *
 * gsplatModifyVS chunk ownership: the chunk is shared with reveal-radial/bloom, dissolve/wipe
 * transitions, compare-clip, relighting, and the standalone mirror-clip chunk (reinstalled every
 * frame by ensureMirrorClipShader whenever the chunk is missing). Policy:
 *  - yield (remove our shader) while any reveal/transition/compare script is enabled; reclaim
 *    the frame after it completes (reveal self-disables and deletes the chunk);
 *  - compose relighting via its `// EFFECT_HOOK` sentinel + `_shaderManagedExternally` protocol,
 *    exactly like gsplat-reveal-radial;
 *  - always embed mirrorClipSnippet and call mirrorClipSplat() so mirrors keep working
 *    (relightingSnippet already includes it, so no double-prepend when composing).
 */

import * as pc from 'playcanvas';
import type { AudioReactiveConfig, AudioReactivePresetName } from '../types';
import { audioAnalyserBus, type AudioBands } from '../dynamic-viewer/audioAnalyserBus';
import { relightingSnippetGLSL, relightingSnippetWGSL } from './gsplat-relighting';
import { mirrorClipSnippetGLSL, mirrorClipSnippetWGSL } from './gsplat-mirror-clip';

const TWO_PI = Math.PI * 2;

export const AUDIO_REACTIVE_DEFAULTS: Required<Omit<AudioReactiveConfig, 'enabled' | 'preset'>> = {
    source: 'scene',
    master: 1,
    pulse: 0.25,
    wave: 0.15,
    waveScale: 0.5,
    waveSpeed: 4,
    wobble: 0.08,
    hueShift: 0,
    hueSpeed: 0.15,
    attack: 0.06,
    release: 0.35,
    idleMotion: true,
};

export const AUDIO_REACTIVE_PRESETS: Record<Exclude<AudioReactivePresetName, 'custom'>, Partial<AudioReactiveConfig>> = {
    subtle: { pulse: 0.25, wave: 0.15, wobble: 0.08, hueShift: 0 },
    club: { pulse: 0.55, wave: 0.4, wobble: 0.2, hueShift: 0.3, waveSpeed: 6, attack: 0.04 },
    mushroom: {
        pulse: 0.8, wave: 0.7, waveScale: 0.8, waveSpeed: 3,
        wobble: 0.6, hueShift: 0.85, hueSpeed: 0.3, release: 0.5,
    },
};

/** Merge defaults + named preset + explicit fields into a fully-resolved config. */
export function resolveAudioReactiveConfig(cfg: AudioReactiveConfig): Required<Omit<AudioReactiveConfig, 'preset'>> & Pick<AudioReactiveConfig, 'preset'> {
    const preset = cfg.preset && cfg.preset !== 'custom' ? AUDIO_REACTIVE_PRESETS[cfg.preset] : undefined;
    return { ...AUDIO_REACTIVE_DEFAULTS, ...preset, ...cfg };
}

const shaderBodyGLSL = /* glsl */ `
uniform float uAudioBass;
uniform float uAudioMid;
uniform float uAudioTreble;
uniform float uAudioLevel;
uniform float uLocalAudioBass;
uniform float uLocalAudioMid;
uniform float uLocalAudioTreble;
uniform float uLocalAudioLevel;
uniform vec3 uArCenter;
uniform vec3 uArLocalCenter;
uniform float uArLocalRadius;
uniform float uArPulse;
uniform float uArWave;
uniform float uArWaveFreq;
uniform float uArWavePhase;
uniform float uArWobble;
uniform float uArWobTime;
uniform float uArHue;
uniform float uArHuePhase;

float arHash(vec3 p) {
    return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
}

// Shared globals (initialized once per vertex in modifySplatCenter)
float g_arGlobalDist;
float g_arLocalDist;
float g_arGlobalRipple;
float g_arLocalRipple;
float g_arLocalFalloff;

void modifySplatCenter(inout vec3 center) {
    vec3 globalRel = center - uArCenter;
    g_arGlobalDist = length(globalRel);
    g_arLocalDist = 0.0;
    g_arLocalRipple = 0.0;
    g_arLocalFalloff = 0.0;
    vec3 globalDir = g_arGlobalDist > 1e-4 ? globalRel / g_arGlobalDist : vec3(0.0, 1.0, 0.0);
    vec3 localRel = vec3(0.0);
    vec3 localDir = vec3(0.0, 1.0, 0.0);

    // Local work is entirely skipped for global-only scenes, and expensive trig is skipped for
    // splats outside the active emitter radius.
    if (uArLocalRadius > 0.0) {
        localRel = center - uArLocalCenter;
        g_arLocalDist = length(localRel);
        g_arLocalFalloff = 1.0 - smoothstep(uArLocalRadius * 0.65, uArLocalRadius, g_arLocalDist);
        if (g_arLocalDist > 1e-4) localDir = localRel / g_arLocalDist;
    }

    // 1) Bass pulse — global tracks breathe from scene center; positional tracks from emitter.
    center += globalDir * g_arGlobalDist * uAudioBass * uArPulse * 0.05;

    // 2) Ripple wave — propagates outward, amplitude rides the mids
    g_arGlobalRipple = sin(g_arGlobalDist * uArWaveFreq - uArWavePhase);
    center += globalDir * g_arGlobalRipple * uAudioMid * uArWave * 0.12;

    // 3) Wobble/warp — cell-hashed phase so neighborhoods sway together
    float globalPh = arHash(floor(globalRel * 0.7)) * 6.28318;
    vec3 globalWob = vec3(
        sin(uArWobTime * 2.3 + globalPh + globalRel.y * 1.7),
        sin(uArWobTime * 1.9 + globalPh * 1.3 + globalRel.x * 1.3),
        sin(uArWobTime * 2.7 + globalPh * 0.7 + globalRel.z * 1.5));
    center += globalWob * uAudioLevel * uArWobble * 0.06;

    if (g_arLocalFalloff > 0.0) {
        center += localDir * g_arLocalDist * uLocalAudioBass * uArPulse * 0.05 * g_arLocalFalloff;
        g_arLocalRipple = sin(g_arLocalDist * uArWaveFreq - uArWavePhase);
        center += localDir * g_arLocalRipple * uLocalAudioMid * uArWave * 0.12 * g_arLocalFalloff;
        float localPh = arHash(floor(localRel * 0.7)) * 6.28318;
        vec3 localWob = vec3(
            sin(uArWobTime * 2.3 + localPh + localRel.y * 1.7),
            sin(uArWobTime * 1.9 + localPh * 1.3 + localRel.x * 1.3),
            sin(uArWobTime * 2.7 + localPh * 0.7 + localRel.z * 1.5));
        center += localWob * uLocalAudioLevel * uArWobble * 0.06 * g_arLocalFalloff;
    }
    rl_casterCenter(center);
}

void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {
    rl_recordSplatShape(rotation, scale);
    mirrorClipSplat(modifiedCenter, scale);
    // Bass makes individual splats swell too (the body of the "breathing")
    float globalScale = uAudioBass * uArPulse * 0.25 + max(g_arGlobalRipple, 0.0) * uAudioMid * uArWave * 0.1;
    float localScale = 0.0;
    if (g_arLocalFalloff > 0.0) {
        localScale = (uLocalAudioBass * uArPulse * 0.25 + max(g_arLocalRipple, 0.0) * uLocalAudioMid * uArWave * 0.1) * g_arLocalFalloff;
    }
    scale *= 1.0 + globalScale + localScale;
}

// Fast hue rotation about the grey axis (Rodrigues) — no HSV round-trip
vec3 arHueRotate(vec3 c, float a) {
    const vec3 k = vec3(0.57735);
    float ca = cos(a);
    float sa = sin(a);
    return c * ca + cross(k, c) * sa + k * dot(k, c) * (1.0 - ca);
}

void modifySplatColor(vec3 center, inout vec4 color) {
    // EFFECT_HOOK
    if (uArHue > 0.001) {
        // Preserve the established whole-scene hue treatment; localized audio layers its own
        // signal-driven rotation only inside the emitter falloff.
        float globalAng = uArHuePhase + g_arGlobalDist * 0.15;
        color.rgb = mix(color.rgb, arHueRotate(color.rgb, globalAng), uArHue);
        float localAmount = uArHue * clamp(uLocalAudioLevel + uLocalAudioTreble, 0.0, 1.0) * g_arLocalFalloff;
        if (localAmount > 0.001) {
            float localAng = uArHuePhase + g_arLocalDist * 0.15;
            color.rgb = mix(color.rgb, arHueRotate(color.rgb, localAng), localAmount);
        }
        color.rgb *= 1.0 + (uAudioTreble + uLocalAudioTreble * g_arLocalFalloff) * uArHue * 0.2;
    }
}
`;

const shaderBodyWGSL = /* wgsl */ `
uniform uAudioBass: f32;
uniform uAudioMid: f32;
uniform uAudioTreble: f32;
uniform uAudioLevel: f32;
uniform uLocalAudioBass: f32;
uniform uLocalAudioMid: f32;
uniform uLocalAudioTreble: f32;
uniform uLocalAudioLevel: f32;
uniform uArCenter: vec3f;
uniform uArLocalCenter: vec3f;
uniform uArLocalRadius: f32;
uniform uArPulse: f32;
uniform uArWave: f32;
uniform uArWaveFreq: f32;
uniform uArWavePhase: f32;
uniform uArWobble: f32;
uniform uArWobTime: f32;
uniform uArHue: f32;
uniform uArHuePhase: f32;

fn arHash(p: vec3f) -> f32 {
    return fract(sin(dot(p, vec3f(127.1, 311.7, 74.7))) * 43758.5453);
}

// Shared globals (initialized once per vertex in modifySplatCenter)
var<private> g_arGlobalDist: f32;
var<private> g_arLocalDist: f32;
var<private> g_arGlobalRipple: f32;
var<private> g_arLocalRipple: f32;
var<private> g_arLocalFalloff: f32;

fn modifySplatCenter(center: ptr<function, vec3f>) {
    let globalRel = *center - uniform.uArCenter;
    g_arGlobalDist = length(globalRel);
    g_arLocalDist = 0.0;
    g_arLocalRipple = 0.0;
    g_arLocalFalloff = 0.0;
    var globalDir = vec3f(0.0, 1.0, 0.0);
    if (g_arGlobalDist > 1e-4) {
        globalDir = globalRel / g_arGlobalDist;
    }
    var localRel = vec3f(0.0);
    var localDir = vec3f(0.0, 1.0, 0.0);

    // Local work is entirely skipped for global-only scenes, and expensive trig is skipped for
    // splats outside the active emitter radius.
    if (uniform.uArLocalRadius > 0.0) {
        localRel = *center - uniform.uArLocalCenter;
        g_arLocalDist = length(localRel);
        g_arLocalFalloff = 1.0 - smoothstep(uniform.uArLocalRadius * 0.65, uniform.uArLocalRadius, g_arLocalDist);
        if (g_arLocalDist > 1e-4) {
            localDir = localRel / g_arLocalDist;
        }
    }

    // 1) Bass pulse — global tracks breathe from scene center; positional tracks from emitter.
    *center += globalDir * g_arGlobalDist * uniform.uAudioBass * uniform.uArPulse * 0.05;

    // 2) Ripple wave — propagates outward, amplitude rides the mids
    g_arGlobalRipple = sin(g_arGlobalDist * uniform.uArWaveFreq - uniform.uArWavePhase);
    *center += globalDir * g_arGlobalRipple * uniform.uAudioMid * uniform.uArWave * 0.12;

    // 3) Wobble/warp — cell-hashed phase so neighborhoods sway together
    let globalPh = arHash(floor(globalRel * 0.7)) * 6.28318;
    let globalWob = vec3f(
        sin(uniform.uArWobTime * 2.3 + globalPh + globalRel.y * 1.7),
        sin(uniform.uArWobTime * 1.9 + globalPh * 1.3 + globalRel.x * 1.3),
        sin(uniform.uArWobTime * 2.7 + globalPh * 0.7 + globalRel.z * 1.5));
    *center += globalWob * uniform.uAudioLevel * uniform.uArWobble * 0.06;

    if (g_arLocalFalloff > 0.0) {
        *center += localDir * g_arLocalDist * uniform.uLocalAudioBass * uniform.uArPulse * 0.05 * g_arLocalFalloff;
        g_arLocalRipple = sin(g_arLocalDist * uniform.uArWaveFreq - uniform.uArWavePhase);
        *center += localDir * g_arLocalRipple * uniform.uLocalAudioMid * uniform.uArWave * 0.12 * g_arLocalFalloff;
        let localPh = arHash(floor(localRel * 0.7)) * 6.28318;
        let localWob = vec3f(
            sin(uniform.uArWobTime * 2.3 + localPh + localRel.y * 1.7),
            sin(uniform.uArWobTime * 1.9 + localPh * 1.3 + localRel.x * 1.3),
            sin(uniform.uArWobTime * 2.7 + localPh * 0.7 + localRel.z * 1.5));
        *center += localWob * uniform.uLocalAudioLevel * uniform.uArWobble * 0.06 * g_arLocalFalloff;
    }
    rl_casterCenter(center);
}

fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {
    rl_recordSplatShape(*rotation, *scale);
    mirrorClipSplat(modifiedCenter, scale);
    // Bass makes individual splats swell too (the body of the "breathing")
    let globalScale = uniform.uAudioBass * uniform.uArPulse * 0.25 + max(g_arGlobalRipple, 0.0) * uniform.uAudioMid * uniform.uArWave * 0.1;
    var localScale = 0.0;
    if (g_arLocalFalloff > 0.0) {
        localScale = (uniform.uLocalAudioBass * uniform.uArPulse * 0.25 + max(g_arLocalRipple, 0.0) * uniform.uLocalAudioMid * uniform.uArWave * 0.1) * g_arLocalFalloff;
    }
    *scale *= 1.0 + globalScale + localScale;
}

// Fast hue rotation about the grey axis (Rodrigues) — no HSV round-trip
fn arHueRotate(c: vec3f, a: f32) -> vec3f {
    let k = vec3f(0.57735);
    let ca = cos(a);
    let sa = sin(a);
    return c * ca + cross(k, c) * sa + k * dot(k, c) * (1.0 - ca);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    // EFFECT_HOOK
    if (uniform.uArHue > 0.001) {
        // Preserve the established whole-scene hue treatment; localized audio layers its own
        // signal-driven rotation only inside the emitter falloff.
        let globalAng = uniform.uArHuePhase + g_arGlobalDist * 0.15;
        var rgb = mix((*color).rgb, arHueRotate((*color).rgb, globalAng), uniform.uArHue);
        let localAmount = uniform.uArHue * clamp(uniform.uLocalAudioLevel + uniform.uLocalAudioTreble, 0.0, 1.0) * g_arLocalFalloff;
        if (localAmount > 0.001) {
            let localAng = uniform.uArHuePhase + g_arLocalDist * 0.15;
            rgb = mix(rgb, arHueRotate(rgb, localAng), localAmount);
        }
        rgb *= 1.0 + (uniform.uAudioTreble + uniform.uLocalAudioTreble * g_arLocalFalloff) * uniform.uArHue * 0.2;
        *color = vec4f(rgb, (*color).a);
    }
}
`;

// Script names that own (or want) the gsplatModifyVS chunk while enabled — we yield to them.
const COMPETITOR_SCRIPTS = [
    'gsplatRevealRadial',
    'gsplatRevealBloom',
    'gsplatDissolveTransition',
    'gsplatWipeTransition',
    'gsplatCompareClip',
] as const;

/** Seconds of near-silence before the idle "breathing" fallback fades in. */
const IDLE_AFTER_S = 2;
const SILENCE_THRESHOLD = 0.02;
/** Fallback discovery for audio paths that did not emit a tracked PlayCanvas slot event. */
const ATTACH_INTERVAL_S = 0.25;
/** Prevent the localized origin from flickering between similarly loud simultaneous emitters. */
const LOCAL_SOURCE_SWITCH_RATIO = 1.15;

export interface SpatialReactiveAudioSource {
    node: AudioNode;
    context: AudioContext;
    entity: pc.Entity;
    radius: number;
}

export interface GsplatAudioReactiveHooks {
    getSplatEntity: () => pc.Entity | null;
    getAudioElements: () => Array<HTMLAudioElement | null | undefined>;
    /** Playing positional media that lives outside PlayCanvas's SoundComponent system. */
    getAdditionalSpatialAudioSources?: () => SpatialReactiveAudioSource[];
}

interface TrackedAudioNode {
    localized: boolean;
    entity: pc.Entity | null;
    radius: number;
}

interface RelightingScriptLike {
    enabled: boolean;
    material: pc.Material | null;
    _shaderManagedExternally: boolean;
    getShaderGLSL: () => string;
    getShaderWGSL: () => string;
}

export class GsplatAudioReactive {
    private app: pc.AppBase;
    private hooks: GsplatAudioReactiveHooks;
    private config: ReturnType<typeof resolveAudioReactiveConfig> | null = null;

    private material: pc.Material | null = null;
    private installedShader: string | null = null;
    private composedWithRelighting = false;

    private time = 0;
    private wavePhase = 0;
    private wobTime = 0;
    private huePhase = 0;
    private silentFor = 0;
    private attachTimer = 0;
    private sceneCenterArray: [number, number, number] = [0, 0, 0];
    private localCenterArray: [number, number, number] = [0, 0, 0];
    private localizedRadius = 0;
    private localizedSourceNode: AudioNode | null = null;
    private trackedAudioNodes = new Map<AudioNode, TrackedAudioNode>();
    private lastSplatEntity: pc.Entity | null = null;

    constructor(app: pc.AppBase, hooks: GsplatAudioReactiveHooks) {
        this.app = app;
        this.hooks = hooks;
    }

    applyConfig(cfg: AudioReactiveConfig): void {
        this.config = resolveAudioReactiveConfig(cfg);
        if (this.config.source === 'mic') {
            this.clearTrackedAudioNodes();
            audioAnalyserBus.detachElements();
        } else {
            audioAnalyserBus.disableMic();
            // Capture a sound that was already playing when the feature was enabled.
            this.refreshSceneAudioSources();
        }
    }

    /**
     * Override strength/speed parameters for this frame onward without re-resolving the
     * config (no audio source re-wiring). Keyframe animation drives the effect through this.
     */
    setAnimatedParams(params: Partial<Record<'master' | 'pulse' | 'wave' | 'wobble' | 'hueShift' | 'waveScale' | 'waveSpeed' | 'hueSpeed', number>>): void {
        if (!this.config) return;
        for (const [key, value] of Object.entries(params)) {
            if (typeof value === 'number' && Number.isFinite(value)) (this.config as Record<string, unknown>)[key] = Math.max(0, value);
        }
    }

    /** Current resolved parameters (null before the first applyConfig). */
    getParams(): Readonly<ReturnType<typeof resolveAudioReactiveConfig>> | null {
        return this.config;
    }

    /** Immediate hook for PlayCanvas SoundSlot play/resume events. */
    trackPlayCanvasInstance(instance: pc.SoundInstance, component: pc.SoundComponent): void {
        if (this.config?.source === 'mic' || !instance.gain) return;
        const context = this.app.systems.sound?.context;
        if (!context) return;
        const localized = component.positional;
        audioAnalyserBus.attachPlayCanvasNode(instance.gain, context, localized);
        this.trackedAudioNodes.set(instance.gain, {
            localized,
            entity: localized ? component.entity : null,
            radius: localized && Number.isFinite(component.maxDistance) && component.maxDistance > 0
                ? component.maxDistance
                : 0,
        });
    }

    /** Immediate hook for PlayCanvas SoundSlot stop/end events. */
    untrackPlayCanvasInstance(instance: pc.SoundInstance): void {
        if (!instance.gain) return;
        this.untrackAudioNode(instance.gain);
    }

    update(dt: number): void {
        const cfg = this.config;
        if (!cfg || !cfg.enabled) {
            this.removeShader();
            return;
        }

        const entity = this.hooks.getSplatEntity();
        const material = this.resolveMaterial(entity);
        if (!material) return;

        if (this.competitorActive(entity)) {
            // A reveal/transition/compare effect owns the chunk — yield until it finishes.
            this.removeShader();
            return;
        }

        // Refresh the effect center when the splat entity changes (swap/teleport).
        if (entity !== this.lastSplatEntity) {
            this.lastSplatEntity = entity;
            this.detectCenter(entity);
        }

        this.reconcileShader(material, entity);

        // Feed the analyser (idempotent; covers late-starting music and narration re-init).
        this.attachTimer -= dt;
        if (this.attachTimer <= 0) {
            this.attachTimer = ATTACH_INTERVAL_S;
            if (cfg.source !== 'mic') {
                for (const el of this.hooks.getAudioElements()) {
                    if (el) audioAnalyserBus.attachElement(el);
                }
                this.refreshSceneAudioSources();
            }
        }

        this.time += dt;
        const analysis = audioAnalyserBus.sample(dt, cfg.attack, cfg.release);
        let { bass, mid, treble, level } = analysis.global;
        let localBass = 0, localMid = 0, localTreble = 0, localLevel = 0;
        const localized = this.selectLocalizedAnalysis(analysis.localized);
        if (localized) {
            localBass = localized.bands.bass;
            localMid = localized.bands.mid;
            localTreble = localized.bands.treble;
            localLevel = localized.bands.level;
        }

        // Idle fallback: gentle breathing when nothing is playing.
        let combinedLevel = Math.max(level, localLevel);
        if (combinedLevel < SILENCE_THRESHOLD) {
            this.silentFor += dt;
        } else {
            this.silentFor = 0;
        }
        if (cfg.idleMotion && this.silentFor > IDLE_AFTER_S) {
            const fadeIn = Math.min(1, (this.silentFor - IDLE_AFTER_S) / 2);
            bass = Math.max(bass, fadeIn * (0.07 + 0.05 * Math.sin(this.time * 0.6)));
            mid = Math.max(mid, fadeIn * (0.04 + 0.03 * Math.sin(this.time * 0.45 + 1.7)));
            level = Math.max(level, fadeIn * 0.05);
        }
        combinedLevel = Math.max(level, localLevel);

        // Phase accumulation — ripple/hue/wobble SPEED rides the music, not just amplitude.
        this.wavePhase += dt * cfg.waveSpeed * (0.3 + combinedLevel);
        this.wobTime += dt * (0.4 + combinedLevel * 1.6);
        this.huePhase += dt * cfg.hueSpeed * TWO_PI * (0.15 + Math.max(treble, localTreble));

        const m = cfg.master;
        material.setParameter('uAudioBass', bass);
        material.setParameter('uAudioMid', mid);
        material.setParameter('uAudioTreble', treble);
        material.setParameter('uAudioLevel', level);
        material.setParameter('uLocalAudioBass', localBass);
        material.setParameter('uLocalAudioMid', localMid);
        material.setParameter('uLocalAudioTreble', localTreble);
        material.setParameter('uLocalAudioLevel', localLevel);
        material.setParameter('uArCenter', this.sceneCenterArray);
        material.setParameter('uArLocalCenter', this.localCenterArray);
        material.setParameter('uArLocalRadius', this.localizedRadius);
        material.setParameter('uArPulse', cfg.pulse * m);
        material.setParameter('uArWave', cfg.wave * m);
        material.setParameter('uArWaveFreq', cfg.waveScale * TWO_PI);
        material.setParameter('uArWavePhase', this.wavePhase);
        material.setParameter('uArWobble', cfg.wobble * m);
        material.setParameter('uArWobTime', this.wobTime);
        material.setParameter('uArHue', cfg.hueShift * m);
        material.setParameter('uArHuePhase', this.huePhase);
        material.update();
    }

    destroy(): void {
        this.removeShader();
        this.clearTrackedAudioNodes();
        audioAnalyserBus.detachElements();
        audioAnalyserBus.disableMic();
        this.config = null;
    }

    // ---- internals ----

    private get lang(): 'glsl' | 'wgsl' {
        return (this.app.graphicsDevice as unknown as { isWebGPU?: boolean })?.isWebGPU ? 'wgsl' : 'glsl';
    }

    private resolveMaterial(entity: pc.Entity | null): pc.Material | null {
        // Unified mode (the norm): template material shared by all splats.
        const unified = (this.app.scene as unknown as { gsplat?: { material?: pc.Material } }).gsplat?.material;
        if (unified) return unified;
        return entity?.gsplat?.material ?? null;
    }

    private getRelightingScript(entity: pc.Entity | null): RelightingScriptLike | null {
        const script = entity?.script as unknown as Record<string, RelightingScriptLike | undefined> | null;
        const relight = script?.gsplatRelighting;
        return relight && relight.enabled ? relight : null;
    }

    private competitorActive(entity: pc.Entity | null): boolean {
        const script = entity?.script as unknown as Record<string, { enabled?: boolean } | undefined> | null;
        if (!script) return false;
        return COMPETITOR_SCRIPTS.some(name => script[name]?.enabled);
    }

    private buildShader(entity: pc.Entity | null): { shader: string; composed: boolean } {
        const lang = this.lang;
        const body = lang === 'wgsl' ? shaderBodyWGSL : shaderBodyGLSL;
        const relight = this.getRelightingScript(entity);
        if (relight) {
            // relightingSnippet already includes mirrorClipSnippet — no separate prepend.
            const snippet = lang === 'wgsl' ? relightingSnippetWGSL : relightingSnippetGLSL;
            const shader = snippet + '\n' + body.replace('// EFFECT_HOOK', 'applyRelighting(center, color);');
            return { shader, composed: true };
        }
        const mirrorSnippet = lang === 'wgsl' ? mirrorClipSnippetWGSL : mirrorClipSnippetGLSL;
        return { shader: mirrorSnippet + body, composed: false };
    }

    private reconcileShader(material: pc.Material, entity: pc.Entity | null): void {
        const relightActive = !!this.getRelightingScript(entity);
        const chunks = material.getShaderChunks(this.lang);
        const needsApply =
            material !== this.material ||
            this.installedShader === null ||
            chunks.get('gsplatModifyVS') !== this.installedShader ||
            relightActive !== this.composedWithRelighting;
        if (!needsApply) return;

        const { shader, composed } = this.buildShader(entity);
        if (composed) {
            // Must be set BEFORE applying so relighting doesn't race us on its own enable.
            this.getRelightingScript(entity)!._shaderManagedExternally = true;
        }
        chunks.set('gsplatModifyVS', shader);
        material.update();
        this.material = material;
        this.installedShader = shader;
        this.composedWithRelighting = composed;
    }

    private removeShader(): void {
        if (!this.material || this.installedShader === null) return;
        const material = this.material;
        const chunks = material.getShaderChunks(this.lang);
        // Only tear down what we installed — someone may have already replaced the chunk.
        const weOwnChunk = chunks.get('gsplatModifyVS') === this.installedShader;

        if (this.composedWithRelighting) {
            const relight = this.getRelightingScript(this.lastSplatEntity ?? this.hooks.getSplatEntity());
            if (relight) {
                if (weOwnChunk) {
                    // Hand the chunk back: restore relighting's standalone shader.
                    relight.material = material;
                    const relightShader = this.lang === 'wgsl' ? relight.getShaderWGSL() : relight.getShaderGLSL();
                    chunks.set('gsplatModifyVS', relightShader);
                    material.update();
                }
                relight._shaderManagedExternally = false;
            } else if (weOwnChunk) {
                chunks.delete('gsplatModifyVS');
                material.update();
            }
        } else if (weOwnChunk) {
            // ensureMirrorClipShader reinstalls the standalone mirror chunk next frame if needed.
            chunks.delete('gsplatModifyVS');
            material.update();
        }

        this.material = null;
        this.installedShader = null;
        this.composedWithRelighting = false;
    }

    private detectCenter(entity: pc.Entity | null): void {
        this.sceneCenterArray = [0, 0, 0];
        try {
            const gsplat = entity?.gsplat as unknown as {
                instance?: { meshInstance?: { aabb?: pc.BoundingBox }; meshInstances?: Array<{ aabb?: pc.BoundingBox }> };
            } | undefined;
            const inst = gsplat?.instance;
            const mi = inst?.meshInstance ?? inst?.meshInstances?.[0];
            const c = mi?.aabb?.center;
            if (c) this.sceneCenterArray = [c.x, c.y, c.z];
        } catch {
            // AABB not available — origin fallback.
        }
    }

    /**
     * Reconcile the analyser side-chains with every playing scene source. Slot events provide the
     * immediate path; this periodic scan catches sounds already playing when reactivity is enabled
     * and any future audio path that does not use the viewer's slot bookkeeping helper.
     */
    private refreshSceneAudioSources(): void {
        const liveNodes = new Set<AudioNode>();
        const components = this.app.root.findComponents('sound') as pc.SoundComponent[];
        for (const component of components) {
            for (const slot of Object.values(component.slots)) {
                for (const instance of slot.instances) {
                    if (!instance.isPlaying) continue;
                    this.trackPlayCanvasInstance(instance, component);
                    if (instance.gain) liveNodes.add(instance.gain);
                }
            }
        }

        for (const source of this.hooks.getAdditionalSpatialAudioSources?.() ?? []) {
            audioAnalyserBus.attachPlayCanvasNode(source.node, source.context, true);
            this.trackedAudioNodes.set(source.node, {
                localized: true,
                entity: source.entity,
                radius: Number.isFinite(source.radius) && source.radius > 0 ? source.radius : 10,
            });
            liveNodes.add(source.node);
        }

        for (const node of this.trackedAudioNodes.keys()) {
            if (!liveNodes.has(node)) this.untrackAudioNode(node);
        }
    }

    /**
     * Use the loudest post-panner signal, not the nearest entity, so the visual origin always
     * belongs to the source whose FFT actually drives it. Entity and unified-splat coordinates
     * are both PlayCanvas world space; source creation already performs the one required Z flip.
     */
    private selectLocalizedAnalysis(
        samples: Array<{ node: AudioNode; bands: AudioBands; rawLevel: number }>
    ): { node: AudioNode; bands: AudioBands; rawLevel: number } | null {
        let selected = this.localizedSourceNode
            ? samples.find(sample => sample.node === this.localizedSourceNode) ?? null
            : null;
        if (selected) {
            const tracked = this.trackedAudioNodes.get(selected.node);
            if (!tracked?.localized || !tracked.entity?.enabled) selected = null;
        }
        for (const sample of samples) {
            const tracked = this.trackedAudioNodes.get(sample.node);
            if (!tracked?.localized || !tracked.entity?.enabled) continue;
            if (!selected || sample.rawLevel > selected.rawLevel * LOCAL_SOURCE_SWITCH_RATIO) selected = sample;
        }

        if (!selected) {
            this.localizedSourceNode = null;
            this.localCenterArray = [...this.sceneCenterArray];
            this.localizedRadius = 0;
            return null;
        }

        const tracked = this.trackedAudioNodes.get(selected.node)!;
        const position = tracked.entity!.getPosition();
        this.localizedSourceNode = selected.node;
        this.localCenterArray = [position.x, position.y, position.z];
        this.localizedRadius = tracked.radius;
        return selected;
    }

    private untrackAudioNode(node: AudioNode): void {
        audioAnalyserBus.detachPlayCanvasNode(node);
        this.trackedAudioNodes.delete(node);
        if (this.localizedSourceNode === node) this.localizedSourceNode = null;
    }

    private clearTrackedAudioNodes(): void {
        for (const node of this.trackedAudioNodes.keys()) {
            audioAnalyserBus.detachPlayCanvasNode(node);
        }
        this.trackedAudioNodes.clear();
        this.localizedSourceNode = null;
        this.localCenterArray = [...this.sceneCenterArray];
        this.localizedRadius = 0;
    }

}
